export const INVITATION_TOKEN_PARAM = "invitation_token";
export const APP_RELATIVE_URL_BASE = "http://arbi.local";
const ASCII_ONLY = /^[\x00-\x7f]*$/;

type VerifyEmailPathParams = {
  email: string;
  pendingAuthenticationToken?: string | null;
};

export function serializeAppUrl(url: URL): string {
  return `${url.pathname}${url.search}${url.hash}`;
}

export function parseAppRelativeUrl(path: string | undefined): URL | null {
  if (!path) return null;

  try {
    const parsedUrl = new URL(path, APP_RELATIVE_URL_BASE);
    if (
      parsedUrl.origin !== APP_RELATIVE_URL_BASE ||
      !path.startsWith("/") ||
      path.startsWith("//")
    ) {
      return null;
    }

    return parsedUrl;
  } catch {
    return null;
  }
}

export function buildInvitePath(token: string): string {
  const params = new URLSearchParams({ [INVITATION_TOKEN_PARAM]: token });

  return `/invite?${params.toString()}`;
}

export function buildInviteUrl(token: string, origin: string): string {
  return new URL(buildInvitePath(token), origin).toString();
}

export function getInvitationTokenFromSearch(search: string): string | null {
  const token = new URLSearchParams(search).get(INVITATION_TOKEN_PARAM);

  return token && token.length > 0 ? token : null;
}

export function getCurrentInvitationToken(): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  return getInvitationTokenFromSearch(window.location.search);
}

export function appendInvitationToken(
  url: string,
  token: string | null,
): string {
  if (token === null) {
    return url;
  }

  const parsedUrl = new URL(url, APP_RELATIVE_URL_BASE);
  parsedUrl.searchParams.set(INVITATION_TOKEN_PARAM, token);

  return serializeAppUrl(parsedUrl);
}

export function appendCurrentInvitationToken(url: string): string {
  return appendInvitationToken(url, getCurrentInvitationToken());
}

export function buildVerifyEmailPath({
  email,
  pendingAuthenticationToken,
}: VerifyEmailPathParams): string {
  const params = new URLSearchParams({ email });
  if (pendingAuthenticationToken) {
    params.set("pat", pendingAuthenticationToken);
  }

  return `/verify?${params.toString()}`;
}

export function buildPostAuthRedirectPath(fallbackPath: string): string {
  const invitationToken = getCurrentInvitationToken();

  return invitationToken ? buildInvitePath(invitationToken) : fallbackPath;
}

export function normalizeInvitationEmailHint(value: string): string {
  return value.trim().toLowerCase();
}

export function invitationEmailsAreDefinitelyDifferent(
  left: string,
  right: string,
): boolean {
  const normalizedLeft = normalizeInvitationEmailHint(left);
  const normalizedRight = normalizeInvitationEmailHint(right);

  return (
    normalizedLeft !== normalizedRight &&
    ASCII_ONLY.test(normalizedLeft) &&
    ASCII_ONLY.test(normalizedRight)
  );
}
